import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Receipt, FileCheck, Mail, MessageSquare, Download, ArrowRightLeft, Zap,
  Edit2, DollarSign, Eye, Link2, CheckCircle2, XCircle, PenLine, Clock, Copy, Send, Lock, Trash2, ReceiptText
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft:     { label: "Draft",     color: "bg-gray-500/15 text-gray-600 dark:text-gray-400 border-gray-500/30" },
  sent:      { label: "Sent",      color: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
  paid:      { label: "Paid",      color: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
  partially_paid: { label: "Partially Paid", color: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" },
  overdue:   { label: "Overdue",   color: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30" },
  cancelled: { label: "Cancelled", color: "bg-gray-500/15 text-gray-500 border-gray-500/30" },
  accepted:  { label: "Accepted",  color: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
  declined:  { label: "Declined",  color: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30" },
  converted: { label: "Converted", color: "bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30" },
};

const InvoiceViewDialog = ({ open, onClose, doc, isDark, onSendEmail, onSendSms, onDownloadPdf, onConvert, connectStatus, onChargeViaStripe, onEdit, onStatusChange, onPreview, onSendForSignature, onRecordPayment, onDeletePayment, onSendReceipt }) => {
  const [sendingSig, setSendingSig] = useState(false);
  if (!doc) return null;
  const st = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
  const isEst = (doc.document_type || "invoice") === "estimate";
  const canCharge = !isEst && doc.status !== "paid" && connectStatus?.connected && connectStatus?.charges_enabled;
  const paidViaStripe = doc.payment_method === "stripe_connect" || doc.payment_method === "stripe";
  const accent = isEst ? "#f97316" : "#3b82f6";

  // Electronic Signature state. "signed" locks the doc from edits.
  const sigStatus = doc.signature_status || (doc.signature_data && doc.status === "accepted" ? "signed" : "not_sent");
  const isSigned = sigStatus === "signed";
  const isSentForSig = sigStatus === "sent_for_signature";
  const docSlug = isEst ? "estimate" : "invoice";
  const docLabel = isEst ? "Estimate" : "Invoice";

  const handleSendSig = async () => {
    if (!onSendForSignature || sendingSig) return;
    setSendingSig(true);
    try { await onSendForSignature(doc); } finally { setSendingSig(false); }
  };

  const statusOptions = isEst
    ? ["draft", "sent", "accepted", "declined"]
    : ["draft", "sent", "partially_paid", "paid", "overdue", "cancelled"];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden rounded-lg p-0" data-testid="view-dialog">
        {/* Header */}
        <div className={`min-w-0 px-4 sm:px-6 pt-6 pb-4 ${isDark ? "border-b border-zinc-800" : "border-b border-gray-100"}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isEst ? "bg-orange-500/15" : "bg-blue-500/15"}`}>
                {isEst ? <FileCheck className="w-5 h-5 text-orange-500" /> : <Receipt className="w-5 h-5 text-blue-500" />}
              </div>
              <div>
                <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{doc.invoice_number}</h2>
                <p className={`text-sm ${isDark ? "text-zinc-400" : "text-gray-500"}`}>{isEst ? "Estimate" : "Invoice"}</p>
              </div>
            </div>
            {doc.status !== "converted" ? (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {(isSigned || isSentForSig) && (
                  <Badge
                    className={`text-[10px] gap-1 ${
                      isSigned
                        ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                        : "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"
                    } border`}
                    data-testid="signature-status-badge"
                  >
                    {isSigned ? <Lock className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {isSigned ? "Signed" : "Awaiting Signature"}
                  </Badge>
                )}
                <select
                  value={doc.status}
                  onChange={(e) => { onStatusChange(doc.invoice_id, e.target.value); }}
                  className={`text-xs font-medium capitalize px-3 py-1.5 rounded-full border cursor-pointer ${st.color}`}
                  style={{ background: "transparent" }}
                  data-testid="view-status-select"
                >
                  {statusOptions.map(s => (
                    <option key={s} value={s} className="capitalize bg-background text-foreground">{s.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
            ) : (
              <Badge className={`${st.color} border text-xs capitalize`}>{st.label}</Badge>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="min-w-0 px-4 sm:px-6 py-4 space-y-5">
          {/* Customer */}
          <div>
            <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Customer</p>
            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{doc.customer_name}</p>
          </div>

          {/* Line Items */}
          <div>
            <p className={`text-[10px] uppercase font-semibold tracking-wider mb-2 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Line Items</p>
            <div className="space-y-1">
              {doc.items?.map((item, i) => {
                const discount = item.discount || 0;
                const lineTotal = item.quantity * item.unit_price * (1 - discount / 100);
                return (
                  <div key={i} className={`py-2 px-3 rounded-lg text-sm ${isDark ? "bg-zinc-800/50" : "bg-gray-50"}`}>
                    <div className="flex justify-between items-start gap-3">
                      {item.photo_url && (
                        <img
                          src={`${API}${item.photo_url}?size=thumb&auth=${localStorage.getItem("token") || ""}`}
                          alt=""
                          className={`w-10 h-10 rounded-md object-cover border flex-shrink-0 ${isDark ? "border-zinc-700" : "border-gray-200"}`}
                          data-testid={`view-item-photo-${i}`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className={`${isDark ? "text-white" : "text-gray-900"}`}>{item.description}</span>
                        <span className={`ml-2 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>x{item.quantity} @ ${item.unit_price?.toFixed(2)}</span>
                        {discount > 0 && <span className="text-orange-500 ml-2 text-xs">-{discount}%</span>}
                      </div>
                      <span className={`font-semibold ml-3 whitespace-nowrap ${isDark ? "text-white" : "text-gray-900"}`}>${lineTotal.toFixed(2)}</span>
                    </div>
                    {item.long_description && (
                      <p className={`mt-1 text-xs whitespace-pre-wrap leading-relaxed ${isDark ? "text-zinc-400" : "text-gray-500"}`}>
                        {item.long_description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals */}
          <div className={`space-y-1.5 pt-3 border-t ${isDark ? "border-zinc-800" : "border-gray-100"}`}>
            <div className="flex justify-between text-sm">
              <span className={isDark ? "text-zinc-400" : "text-gray-500"}>Subtotal</span>
              <span className={isDark ? "text-zinc-300" : "text-gray-700"}>${doc.subtotal?.toFixed(2)}</span>
            </div>
            {(doc.discount_total || 0) > 0 && (
              <div className="flex justify-between text-sm text-orange-500">
                <span>{doc.discount_type === "flat" ? "Discount (flat)" : (doc.discount_value || 0) > 0 ? `Discount (${doc.discount_value}%)` : "Discount"}</span><span>-${doc.discount_total?.toFixed(2)}</span>
              </div>
            )}
            {doc.tax_rate > 0 && (doc.non_taxable_subtotal || 0) > 0 && (
              <div className="flex justify-between text-sm" data-testid="view-taxable-subtotal">
                <span className={isDark ? "text-zinc-400" : "text-gray-500"}>Taxable Subtotal</span>
                <span className={isDark ? "text-zinc-300" : "text-gray-700"}>${(doc.taxable_subtotal || 0).toFixed(2)}</span>
              </div>
            )}
            {doc.tax_rate > 0 && (
              <div className="flex justify-between text-sm">
                <span className={isDark ? "text-zinc-400" : "text-gray-500"}>Tax ({doc.tax_rate}%)</span>
                <span className={isDark ? "text-zinc-300" : "text-gray-700"}>${doc.tax_amount?.toFixed(2)}</span>
              </div>
            )}
            {doc.credit_fee_enabled && (doc.credit_fee_amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className={isDark ? "text-zinc-400" : "text-gray-500"}>
                  {doc.credit_fee_type === "percentage" ? `Credit Fee (${doc.credit_fee_value}%)` : "Credit Fee"}
                </span>
                <span className={isDark ? "text-zinc-300" : "text-gray-700"}>${doc.credit_fee_amount?.toFixed(2)}</span>
              </div>
            )}
            <div className={`flex justify-between pt-2.5 mt-1 border-t ${isDark ? "border-zinc-700" : "border-gray-200"}`}>
              <span className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>Total</span>
              <span className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>${doc.total?.toFixed(2)}</span>
            </div>
            {!isEst && (doc.deposit_required || 0) > 0 && (
              <div className="flex justify-between text-sm pt-1" data-testid="deposit-required-row">
                <span className="text-muted-foreground">Deposit required</span>
                <span className={`font-medium ${(doc.amount_paid || 0) >= (doc.deposit_required || 0) ? "text-emerald-500" : "text-foreground"}`}>
                  ${(doc.deposit_required || 0).toFixed(2)}{(doc.amount_paid || 0) >= (doc.deposit_required || 0) ? " (collected)" : ""}
                </span>
              </div>
            )}
            {!isEst && (doc.amount_paid || 0) > 0 && doc.status !== "paid" && (
              <div data-testid="payment-balance-block">
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-500 font-medium">Paid to date</span>
                  <span className="text-emerald-500 font-semibold" data-testid="paid-to-date-amount">-${(doc.amount_paid || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-amber-500 font-bold">Balance Due</span>
                  <span className="text-amber-500 font-bold" data-testid="balance-due-amount">${Math.max((doc.total || 0) - (doc.amount_paid || 0), 0).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment History (deposits / partial payments) */}
          {!isEst && (doc.payments || []).length > 0 && (
            <div data-testid="payment-history">
              <p className={`text-[10px] uppercase font-semibold tracking-wider mb-2 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Payment History</p>
              <div className="space-y-1.5">
                {doc.payments.map((p, i) => (
                  <div
                    key={p.payment_id || i}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 ${isDark ? "bg-emerald-500/5 border border-emerald-500/15" : "bg-emerald-50/60 border border-emerald-100"}`}
                    data-testid={`payment-row-${i}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-emerald-500">${(p.amount || 0).toFixed(2)}
                        {p.method && <span className={`font-normal text-xs ml-1.5 ${isDark ? "text-zinc-400" : "text-gray-500"}`}>{p.method}</span>}
                      </p>
                      <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                        {p.payment_date || (p.created_at || "").slice(0, 10)}
                        {p.reference && ` · Ref: ${p.reference}`}
                        {p.collected_by_name && ` · by ${p.collected_by_name}`}
                      </p>
                      {p.note && <p className={`text-[11px] italic ${isDark ? "text-zinc-500" : "text-gray-400"}`}>{p.note}</p>}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0 ml-2">
                      {onSendReceipt && p.payment_id && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-emerald-500" title="Send receipt" onClick={() => onSendReceipt(doc, p)} data-testid={`send-receipt-payment-${i}`}>
                          <ReceiptText className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {onDeletePayment && p.payment_id && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500" title="Delete payment" onClick={() => onDeletePayment(doc, p.payment_id)} data-testid={`delete-payment-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment info */}
          {doc.payment_method && (
            <div>
              <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Payment Method</p>
              <div className="flex items-center gap-2">
                <p className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{doc.payment_method}</p>
                {paidViaStripe && (
                  <Badge className="bg-[#635BFF]/15 text-[#635BFF] border-[#635BFF]/30 text-[10px] gap-1">Stripe</Badge>
                )}
              </div>
            </div>
          )}

          {/* Notes & Terms */}
          {doc.payment_terms && (
            <div>
              <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Payment Terms</p>
              <p className={`text-sm ${isDark ? "text-zinc-400" : "text-gray-600"}`}>{doc.payment_terms}</p>
            </div>
          )}
          {doc.notes && (
            <div>
              <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Notes</p>
              <p className={`text-sm ${isDark ? "text-zinc-400" : "text-gray-600"}`}>{doc.notes}</p>
            </div>
          )}

          {/* Approval / Rejection / Signed Timeline */}
          {(doc.approved_at || doc.rejected_at || doc.signature_data || isSigned) && (
            <div className={`rounded-lg p-4 space-y-3 ${
              doc.status === "accepted" || isSigned
                ? isDark ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-200"
                : doc.status === "declined"
                ? isDark ? "bg-red-500/10 border border-red-500/20" : "bg-red-50 border border-red-200"
                : isDark ? "bg-zinc-800/50 border border-zinc-700" : "bg-gray-50 border border-gray-200"
            }`} data-testid="approval-timeline">
              <div className="flex items-center gap-2 flex-wrap">
                {(doc.status === "accepted" || isSigned) ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}
                <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {isSigned && !isEst ? "Signed by Customer"
                    : doc.status === "accepted" ? "Approved by Customer"
                    : "Declined by Customer"}
                </span>
                <span className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`} data-testid="signed-at-text">
                  {doc.signature_signed_at || doc.approved_at || doc.rejected_at
                    ? new Date(doc.signature_signed_at || doc.approved_at || doc.rejected_at).toLocaleString()
                    : ""}
                </span>
              </div>
              {(doc.signature_signer_name || doc.signature_signer_email) && (
                <div className={`text-xs ${isDark ? "text-zinc-400" : "text-gray-600"}`} data-testid="signer-info">
                  {doc.signature_signer_name && <span className="font-medium">{doc.signature_signer_name}</span>}
                  {doc.signature_signer_email && <span> · {doc.signature_signer_email}</span>}
                  {doc.signature_ip && <span className={isDark ? "text-zinc-500" : "text-gray-400"}> · IP {doc.signature_ip}</span>}
                </div>
              )}
              {doc.rejection_reason && (
                <p className={`text-sm italic ${isDark ? "text-zinc-400" : "text-gray-600"}`}>
                  Reason: &quot;{doc.rejection_reason}&quot;
                </p>
              )}
              {doc.signature_data && (
                <div>
                  <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1.5 flex items-center gap-1 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                    <PenLine className="w-3 h-3" /> Customer Signature
                  </p>
                  <div className={`rounded-lg p-3 ${isDark ? "bg-white/5" : "bg-white"}`}>
                    <img src={doc.signature_data} alt="Customer signature" className="max-h-16 object-contain" data-testid="signature-image" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sent for Signature (awaiting customer) */}
          {isSentForSig && !isSigned && (
            <div className={`rounded-lg p-4 flex items-start gap-3 ${
              isDark ? "bg-amber-500/10 border border-amber-500/20" : "bg-amber-50 border border-amber-200"
            }`} data-testid="awaiting-signature-banner">
              <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>Awaiting Customer Signature</p>
                <p className={`text-xs mt-0.5 ${isDark ? "text-zinc-400" : "text-gray-600"}`}>
                  Sent {doc.signature_sent_at ? new Date(doc.signature_sent_at).toLocaleString() : "recently"}.
                  The customer received an email with a secure signature link.
                </p>
              </div>
            </div>
          )}

          {/* Public Estimate / Invoice Link */}
          {doc.public_token && (
            <div>
              <p className={`text-[10px] uppercase font-semibold tracking-wider mb-1.5 flex items-center gap-1 ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                <Link2 className="w-3 h-3" /> {isEst ? "Customer Approval Link" : "Customer Signature Link"}
              </p>
              <div className="flex items-center gap-2">
                <code className={`flex-1 text-xs p-2 rounded-lg truncate ${isDark ? "bg-zinc-800 text-zinc-300" : "bg-gray-100 text-gray-600"}`} data-testid="public-signature-link">
                  {`${window.location.origin}/${docSlug}/${doc.public_token}`}
                </code>
                <Button variant="outline" size="sm" className="h-8 px-2 flex-shrink-0" data-testid="copy-estimate-link"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/${docSlug}/${doc.public_token}`); toast.success("Link copied!"); }}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Actions footer */}
        <div className={`min-w-0 px-4 sm:px-6 py-4 border-t flex flex-wrap gap-2 ${isDark ? "border-zinc-800" : "border-gray-100"}`}>
          {!isEst && onRecordPayment && !["paid", "cancelled"].includes(doc.status) && (
            <Button
              size="sm"
              onClick={() => onRecordPayment(doc)}
              className="bg-emerald-600 text-white hover:bg-emerald-700 gap-1.5"
              data-testid="record-payment-btn"
            >
              <DollarSign className="w-3.5 h-3.5" /> Record Payment
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(doc)}
            disabled={isSigned}
            className="gap-1.5"
            data-testid="view-edit-btn"
            title={isSigned ? "Locked: document has been signed" : ""}
          >
            {isSigned ? <Lock className="w-3.5 h-3.5" /> : <Edit2 className="w-3.5 h-3.5" />} Edit
          </Button>
          {onPreview && (
            <Button variant="outline" size="sm" onClick={() => { onPreview(doc); onClose(); }} className="gap-1.5" data-testid="view-preview-btn">
              <Eye className="w-3.5 h-3.5" /> Preview
            </Button>
          )}
          {/* Estimates: Email/SMS hidden — "Send for Signature" is the single
              send action (its modal offers the Email/SMS delivery choice). */}
          {!isEst && (
            <Button variant="outline" size="sm" onClick={() => onSendEmail(doc)} className="gap-1.5" data-testid="view-email-btn">
              <Mail className="w-3.5 h-3.5" /> Email
            </Button>
          )}
          {!isEst && onSendSms && (
            <Button variant="outline" size="sm" onClick={() => onSendSms(doc)} className="gap-1.5 hover:text-emerald-500 hover:border-emerald-500/40" data-testid="view-sms-btn">
              <MessageSquare className="w-3.5 h-3.5" /> SMS
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onDownloadPdf(doc)} className="gap-1.5" data-testid="view-pdf-btn">
            <Download className="w-3.5 h-3.5" /> PDF
          </Button>
          {/* Send for Signature — works for invoices AND estimates.
              Hidden once signed (no point re-asking) and on converted estimates. */}
          {onSendForSignature && !isSigned && doc.status !== "converted" && (
            <Button
              size="sm"
              onClick={handleSendSig}
              disabled={sendingSig}
              className="bg-emerald-600 text-white hover:bg-emerald-700 gap-1.5"
              data-testid="view-send-for-signature-btn"
            >
              {sendingSig ? <Clock className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {isSentForSig ? "Resend Signature Link" : "Send for Signature"}
            </Button>
          )}
          {canCharge && (
            <Button size="sm" className="bg-[#635BFF] text-white hover:bg-[#5349E0] gap-1.5" onClick={() => onChargeViaStripe(doc)} data-testid="view-charge-stripe-btn">
              <Zap className="w-3.5 h-3.5" /> Charge via Stripe
            </Button>
          )}
          {isEst && doc.status !== "converted" && (
            <Button size="sm" className="bg-purple-600 text-white hover:bg-purple-700 gap-1.5" onClick={() => { onConvert(doc.invoice_id); onClose(); }} data-testid="view-convert-btn">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Convert to Invoice
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceViewDialog;
